import { forwardRef, useEffect, useState } from 'react'
import { Dropdown, Modal, Button, Spinner, Form } from 'react-bootstrap'
import { DownTriangle, Tick, Close, Heart, Unfriend, BrokenHeart, Flag } from 'assets/icons/components'
import { FriendServices, UserSettingsServices } from 'services'
import { ECHO_FRIEND_CHANGE, REFRESH_FRIENDS, REFRESH_HISTORY } from 'constants/realtimeEvent'
import { toast } from 'components/common/Toast'
import { setRegisterModal, acceptFriendRequest, setFriends } from 'actions'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import i18n from 'config/i18n'
import PerfectScrollbar from 'components/common/PerfectScrollbar/PerfectScrollbar'
import { Avatar } from 'components/common'
import {
  Header,
  ContentContainer,
  Count
} from './style'
import { RecentLineWrapperDiv, ReportModal, RowActionsMenu } from 'components//common/index'
import { HistoryServices, MessageServices } from 'services/index'
import { addConversation, setActiveConversation } from 'actions/chat'
import { setNewEvent } from 'actions/echo'
import { setUpgradeModal } from 'actions/mainContent'

const FriendToggle = forwardRef(({ children, onClick }, ref) => {
  return (
    <div
      className='filter__label'
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}>
      {children} <DownTriangle className='filter__icon' />
    </div>
  )
})

const Friends = (props) => {
  const { friendRequests, allFriends, conversations,
    openChat, dispatch, user } = props
  const { t } = useTranslation('live', { i18n })
  const [friendsList, setFriendsList] = useState([])
  const [filter, setFilter] = useState('Online')
  const [isShowBlockModal, setIsShowBlockModal] = useState(false)
  const [isShowUnfriendModal, setIsShowUnfriendModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [askUser, setAskUser] = useState('')
  const [loading, setLoading] = useState(null)
  const [isAbleNext, setIsAbleNext] = useState(true)
  const [queries, setQueries] = useState({ page: 2, per_page: 10 })

  useEffect(() => {
    if (allFriends) {
      handleApplyFriendListFilter(allFriends)
    }
  }, [allFriends, filter])

  const handleApplyFriendListFilter = (relationships) => {
    switch (filter) {
      case 'All friends':
        setFriendsList(relationships)
        break;
      case 'Online':
        setFriendsList(relationships.filter(friend => friend?.sender?.online))
        break;
      case 'Favorites':
        setFriendsList(relationships.filter(friend => friend?.is_favorite === true))
        break;
      default:
        break;
    }
  }

  const declineRequest = async (event, request) => {
    event.stopPropagation()
    if (loading === `decline-${request?.id}`) return;

    setLoading(`decline-${request?.id}`)

    let { data, error } = await FriendServices.cancelFriendRequest(request?.id)
    if (error) {
      toast.error(error?.message)
    }
    
    setLoading(null)
  }

  const acceptRequest = async (request) => {
    if (user?.account_tier === 0 && user?.gender === 0) {
      dispatch(setRegisterModal(true))
      return
    }

    setLoading(`accept-${request?.id}`)

    let { friend, error } = await FriendServices.acceptFriendRequest(request?.id)
    if (error) {
      if (error?.response?.data?.exception === 'REQUIRED_PREMIUM') {
        dispatch(setUpgradeModal(true))
      }
      // toast.error(error?.message)
    } else {
      dispatch(acceptFriendRequest(friend))
      dispatch(setNewEvent({ event: ECHO_FRIEND_CHANGE, relationship: friend }))
    }

    setLoading(null)
  }

  const markFavorite = async (event, relationshipId) => {
    event.stopPropagation()

    let { friend, error } = await FriendServices.toggleFavorite(relationshipId)
    if (error) {
      toast.error(error?.message)
    } else {
      let relationships = allFriends.map(relationship => relationship.id === friend.id ? friend : relationship)
      setFriends(relationships)
      handleApplyFriendListFilter(relationships)
    }
  }

  const unfriend = async () => {
    setIsLoading(true)
    let { data, error } = await FriendServices.cancelFriendRequest(askUser?.id)
    setIsLoading(false)
    setIsShowUnfriendModal(false)

    if (error) {
      toast.error(error?.message)
    } else {
      let relationships = allFriends.filter(relationship => relationship.id !== askUser?.id)
      setFriends(relationships)
      handleApplyFriendListFilter(relationships)
    }
  }

  const blockAsk = async (e, user) => {
    e.stopPropagation()
    setAskUser(user)
    setIsShowBlockModal(true)
  }

  const handleCloseBlockModal = () => {
    setIsShowBlockModal(false)
    setTimeout(() => {
      setAskUser('')
    }, 200)
  }

  const unfriendAsk = async (e, friend) => {
    e.stopPropagation()
    setAskUser(friend)
    setIsShowUnfriendModal(true)
  }

  const handleCloseUnfriendModal = () => {
    setIsShowUnfriendModal(false)
    setTimeout(() => {
      setAskUser('')
    }, 200)
  }

  const handleOpenChat = async (friend) => {
    const activeConversation = conversations.find(conversation => {
      return (
        conversation?.participant?.uid === friend?.sender?.uid ||
        friend?.conversation?.token === conversation?.token
      );
    });

    if (activeConversation) {
      openChat(activeConversation)
    } else {
      if (friend?.conversation?.token) {
        const { conversation } = await HistoryServices.getConversation(friend?.conversation?.token)

        if (conversation) {
          openChat(conversation)
          updateFriendsList(friend, conversation)
          dispatch(addConversation(conversation))
        }
      } else {
        const { conversation } = await MessageServices.getConversation(friend?.sender?.uid)

        if (conversation) {
          openChat(conversation)
          updateFriendsList(friend, conversation)
          dispatch(addConversation(conversation))
        }
      }
    }
  }

  const updateFriendsList = (friend, conversation) => {
    let friends = allFriends.map(item => {
      if (item.id === friend.id) {
        item.conversation = {
          token: conversation.token
        }
      }

      return item
    })

    dispatch(setFriends(friends))
  }

  const handleLoadNextPage = async () => {
    if (isLoading || !isAbleNext || !allFriends.length || allFriends.length < 10) return;

    setIsLoading(true)
    let { friends } = await FriendServices.getFriends(queries)

    if (friends && friends.length) {
      friends = friends.filter(friend => {
        return !allFriends.some(existingFriend => existingFriend.id === friend.id)
      })
      friends = [...friends, ...allFriends].sort(item => item?.sender?.online ? -1 : 1)

      dispatch(setFriends(friends))
      setQueries({ ...queries, page: queries.page + 1 })
    } else {
      setIsAbleNext(false)
    }
    setIsLoading(false)
  }

  return (
    <>
      <Header
        className='message__accordion-toggle'>
        <div className='accordion-toggle__heading'>
          <div className='heading__label'>{ t('Friends') }</div>
          { friendRequests?.length ? <Count>{ friendRequests.length }</Count> : <></> }
        </div>
        <div className='messages__filter'>
          <Dropdown drop='start'>
            <Dropdown.Toggle as={FriendToggle} >
              {t(filter)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setFilter('All friends')} >
                {t('All friends')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter('Online')}>
              {t('Online')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter('Favorites')}>
                {t('Favorites')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Header>
      <Modal show={isShowUnfriendModal} onHide={handleCloseUnfriendModal} className='ask-modal'>
        <Modal.Header closeButton>
          <Modal.Title>{t('Unfriend')} { askUser?.sender?.name }?</Modal.Title>
        </Modal.Header>
        <Modal.Body >
          <div className='ask-modal__text'>
            { t('Are you sure want to unfriend {{name}}?', { name: askUser?.sender?.name }) }
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className='ask-button' variant='secondary' onClick={() => !isLoading && handleCloseUnfriendModal()}>
            {t('Cancel')}
          </Button>
          <Button className='ask-button' variant='primary' onClick={() => !isLoading && unfriend()}>
            <>{isLoading && <Spinner
                as='span' animation='border' size='sm' role='status'
              />} <span>{t('Unfriend')}</span></>
          </Button>
        </Modal.Footer>
      </Modal>

      <ReportModal
        show={isShowBlockModal}
        hide={handleCloseBlockModal}
        user={askUser}
      >
      </ReportModal>

      <ContentContainer>
        <PerfectScrollbar
          options={{suppressScrollX:true, useBothWheelAxes:false}}
          onYReachEnd={() => handleLoadNextPage()}
        >
          {friendRequests?.length > 0 && (
            <>
              {friendRequests.map(request => (
                <RecentLineWrapperDiv
                  className='messages__user-wrapper messages__user-wrapper--requests'
                  key={request?.id}
                  onClick={() => handleOpenChat(request)}>
                  <div className='user-wrapper__info'>
                    {/* <div
                      className='info__avatar'
                      style={{ backgroundImage: `url(${request?.sender?.avatar})` }}>
                    </div> */}
                    <Avatar user={request?.sender} />
                    <div className='info__conversation'>
                      <div className='conversation__upper'>
                        <div className='upper__username'>
                          <div className='username__name'>
                            {request?.sender?.name}
                          </div>
                        </div>
                        <div className='upper__actions'>
                          <div
                            className={`actions__icon actions__icon--decline ${loading === `decline-${request?.id}` ? 'actions__icon--loading' : ''}`}
                            onClick={(event) => declineRequest(event, request)}>
                            {loading === `decline-${request?.id}` ?
                              <Spinner animation='border' size='sm' />
                              : <Close className='icon__icon' />}
                          </div>
                          <div
                            className={`actions__icon actions__icon--accept ${loading === `accept-${request?.id}` ? 'actions__icon--loading' : ''}`}
                            onClick={() => loading === `accept-${request?.id}` ? '' : acceptRequest(request)}>
                            {loading === `accept-${request?.id}` ?
                              <Spinner animation='border' size='sm' />
                              : <Tick className='icon__icon' />}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </RecentLineWrapperDiv>
              ))}
            </>
          )}

          {friendsList && friendsList.map(friend => (
            <RecentLineWrapperDiv
              key={friend.id}
              className='messages__user-wrapper'
              onClick={() => handleOpenChat(friend)}>
              <div className='user-wrapper__info'>
                <Avatar user={friend?.sender} />
                <div className='info__conversation'>
                  <div className='conversation__upper'>
                    <div className='upper__username'>
                      <div className='username__name'>
                        {friend?.sender?.name}
                      </div>
                      {friend?.relationship?.is_favorite && <div className='username__favorite'>
                        <Heart className='favorite__icon' />
                      </div>}
                    </div>
                    <RowActionsMenu
                      items={[
                        friend?.is_favorite
                          ? { icon: <BrokenHeart className='item-icon item-icon--block' />, label: t('Unmark Favorite'), onClick: (event) => markFavorite(event, friend?.id) }
                          : { icon: <Heart className='item-icon' />, label: t('Mark Favorite'), onClick: (event) => markFavorite(event, friend?.id) },
                        { icon: <Unfriend className='item-icon item-icon--unfriend' />, label: t('Unfriend'), onClick: (e) => unfriendAsk(e, friend) },
                        { icon: <Flag className='item-icon item-icon--block' />, label: t('Report/Block'), onClick: (e) => blockAsk(e, friend?.sender) },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </RecentLineWrapperDiv>
          ))}
        </PerfectScrollbar>
      </ContentContainer>
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.connectionReducer.user,
  allFriends: state.relationshipReducer.friends,
  friendRequests: state.relationshipReducer.friendRequests,
  conversations: state.chatReducer.conversations,
})

export default connect(mapStateToProps)(Friends)
